import { useState, useEffect } from 'react'
import { supabase } from '../../api/supabase'

interface JobMilestone {
  id: string
  milestone_code: string
  milestone_name: string
  occurred_at: string
  notes: string | null
}

interface MilestonesTabProps {
  jobId: string
  jobRef: string
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function TimelineItem({ text, sub }: { text: string; sub: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 bg-blue-500" />
      <div>
        <p className="text-sm text-gray-900 dark:text-white">{text}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{sub}</p>
      </div>
    </div>
  )
}

// #11/#300: a starter set, free-text editable rather than a locked-down enum.
const COMMON_MILESTONES = [
  { code: 'ARRIVED', name: 'Arrived' },
  { code: 'CUSTOMS_CLEARED', name: 'Customs Cleared' },
  { code: 'DELIVERED', name: 'Delivered' },
]

export default function MilestonesTab({ jobId }: MilestonesTabProps) {
  const [milestones, setMilestones] = useState<JobMilestone[]>([])
  const [loading, setLoading] = useState(true)
  const [code, setCode] = useState(COMMON_MILESTONES[0].code)
  const [name, setName] = useState(COMMON_MILESTONES[0].name)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true)
    const { data, error: loadErr } = await supabase
      .from('job_milestones')
      .select('*')
      .eq('job_id', jobId)
      .order('occurred_at', { ascending: false })
    // A failed read used to look exactly like "no milestones yet" (that is how a missing SELECT grant stayed invisible): say so.
    setError(loadErr ? `Could not load milestones: ${loadErr.message}` : '')
    setMilestones(data || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [jobId])

  const handleAdd = async () => {
    if (!code.trim() || !name.trim()) return
    setSaving(true)
    setError('')
    try {
      // Our own shipment-progress tracking: a plain insert into job_milestones (nothing is sent to BoxTop any more).
      // RLS: ADMIN / OPERATIONS only (see migration 20261103a for the INSERT grant).
      const { error: insertErr } = await supabase.from('job_milestones').insert({
        job_id: jobId, milestone_code: code, milestone_name: name, occurred_at: new Date().toISOString(),
      })
      if (insertErr) throw new Error(insertErr.message)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record milestone')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Milestone Timeline</h2>
        </div>
        <div className="px-5 py-4">
          {loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : milestones.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500">No milestones recorded yet.</p>
          ) : (
            <div className="space-y-3">
              {milestones.map(m => (
                <TimelineItem key={m.id} text={m.milestone_name} sub={fmtDateTime(m.occurred_at)} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Record Milestone</h2>
        <p className="text-xs text-gray-400">Records a step in this shipment's progress.</p>
        <div className="grid grid-cols-2 gap-3">
          <select
            value={code}
            onChange={e => {
              const found = COMMON_MILESTONES.find(m => m.code === e.target.value)
              setCode(e.target.value)
              if (found) setName(found.name)
            }}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
          >
            {COMMON_MILESTONES.map(m => <option key={m.code} value={m.code}>{m.name}</option>)}
          </select>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
            placeholder="Milestone name"
          />
        </div>
        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
        <button
          onClick={handleAdd}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Recording…' : 'Record Milestone'}
        </button>
      </div>
    </div>
  )
}
